import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { getCityBySlug } from '../data/cities';

function slugToDisplayName(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const API_BASE = import.meta.env.VITE_API_URL
  ? `https://${import.meta.env.VITE_API_URL}`
  : 'https://api.helpped.com';

const TESTFLIGHT_URL = 'https://testflight.apple.com/join/helpped';

interface Article {
  slug: string;
  title: string;
  meta_description: string;
  body: string;
  word_count: number | null;
  article_type: string;
  published_at: string | null;
  city_slug: string;
}

function markdownToHtml(md: string): string {
  return md
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-gray-900 mt-5 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm,  '<h2 class="text-lg font-bold text-gray-900 mt-6 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm,   '<h1 class="text-xl font-bold text-gray-900 mt-6 mb-2">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,    '<em>$1</em>')
    .replace(/\n\n/g, '</p><p class="mb-4 leading-relaxed text-gray-700">')
    .replace(/^/,  '<p class="mb-4 leading-relaxed text-gray-700">')
    .replace(/$/,  '</p>');
}

function pubDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

export default function ArticlePage() {
  const { citySlug, slug } = useParams<{ citySlug: string; slug: string }>();
  const city = getCityBySlug(citySlug ?? '');
  const displayName = city?.name ?? slugToDisplayName(citySlug ?? '');

  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  usePageMeta(
    article ? `${article.title} | Helpped` : `${displayName} Council | Helpped`,
    article?.meta_description ?? '',
    city ? { placename: `${city.name}, Washington`, lat: city.lat, lon: city.lon } : undefined,
  );

  useEffect(() => {
    if (!citySlug || !slug) return;
    fetch(`${API_BASE}/api/cities/${citySlug}/council/${slug}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.ok ? r.json() : null;
      })
      .then((d) => { if (d) setArticle(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [citySlug, slug]);

  if (!citySlug) return null;
  if (notFound) return <Navigate to={`/cities/${citySlug}/council`} replace />;

  const newsArticleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.meta_description,
    datePublished: article.published_at,
    dateModified:  article.published_at,
    author: { '@type': 'Organization', name: 'Helpped', url: 'https://helpped.com' },
    publisher: { '@id': 'https://helpped.com/#organization' },
    about: {
      '@type': 'City',
      name: displayName,
      addressRegion: 'WA',
      addressCountry: 'US',
      geo: city ? { '@type': 'GeoCoordinates', latitude: city.lat, longitude: city.lon } : undefined,
    },
    url: `https://helpped.com/cities/${citySlug}/council/${slug}`,
    wordCount: article.word_count,
  } : null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',    item: 'https://helpped.com/' },
      { '@type': 'ListItem', position: 2, name: displayName, item: `https://helpped.com/cities/${citySlug}` },
      { '@type': 'ListItem', position: 3, name: 'Council', item: `https://helpped.com/cities/${citySlug}/council` },
      { '@type': 'ListItem', position: 4, name: article?.title ?? 'Article', item: `https://helpped.com/cities/${citySlug}/council/${slug}` },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {newsArticleSchema && (
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify([newsArticleSchema, breadcrumbSchema]) }} />
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-2 text-sm">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: 'hsl(var(--primary))' }}>H</div>
            <span className="font-bold text-gray-900">Helpped</span>
          </Link>
          <span className="text-gray-300 mx-1">/</span>
          {city && <Link to={`/cities/${citySlug}`} className="text-gray-500 hover:text-gray-800 hidden sm:block">{displayName}</Link>}
          <span className="text-gray-300 mx-1 hidden sm:block">/</span>
          <Link to={`/cities/${citySlug}/council`} className="text-gray-500 hover:text-gray-800">Council</Link>

          <a href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer"
            className="ml-auto text-xs font-semibold text-white px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity"
            style={{ background: 'hsl(var(--primary))' }}>
            Get the app
          </a>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10 w-full flex-1">
        {loading ? (
          <div className="animate-pulse space-y-4 pt-4">
            <div className="h-6 bg-gray-100 rounded w-3/4" />
            <div className="h-4 bg-gray-100 rounded w-1/3" />
            <div className="mt-6 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-4 bg-gray-100 rounded" style={{ width: `${80 + (i % 3) * 7}%` }} />
              ))}
            </div>
          </div>
        ) : article ? (
          <>
            {/* Type badge */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                style={{
                  background: article.article_type === 'meeting' ? 'hsl(38 90% 94%)' : 'hsl(158 68% 94%)',
                  color: article.article_type === 'meeting' ? 'hsl(38 60% 35%)' : 'hsl(158 68% 28%)',
                }}>
                {article.article_type === 'meeting' ? 'Meeting Preview' : 'Council Vote'}
              </span>
              <span className="text-xs text-gray-400">
                {displayName}, WA ·{pubDate(article.published_at)}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight mb-4">
              {article.title}
            </h1>

            {/* Article body */}
            <div
              className="prose-sm text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(article.body) }}
            />

            {/* Inline CTA */}
            <div className="mt-10 p-5 rounded-2xl border border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div>
                <p className="text-sm font-bold text-gray-900 mb-0.5">
                  Follow {displayName} civic news on your phone
                </p>
                <p className="text-xs text-gray-500">
                  Get council votes, meeting previews, and neighborhood updates — free on iOS.
                </p>
              </div>
              <a href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white text-xs hover:opacity-90 transition-opacity"
                style={{ background: '#000' }}>
                <svg width="12" height="15" viewBox="0 0 814 1000" fill="white">
                  <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 384.7 8.1 191.3 8.1 106.2c0-134.9 87.6-206.1 173.4-206.1 46.9 0 85.9 30.7 114.8 30.7 27.5 0 71.5-32.7 123.1-32.7 20.8 0 108.2 1.9 160.8 67.1zm-138.9-141.9C617.1 138.5 651.9 89 651.9 39.5c0-6.4-.7-12.8-1.3-19.3-47.3 1.9-101.7 31.3-134.2 71.6-29.4 35.2-57.8 86.4-57.8 135.7 0 6.4.7 12.8 1.3 19.3 4.5.7 9 1.3 13.5 1.3 44.2 0 95.9-27.5 127.8-69.8z"/>
                </svg>
                Download on TestFlight
              </a>
            </div>
          </>
        ) : null}
      </main>

      <footer className="px-6 py-6 border-t border-gray-100">
        <div className="max-w-3xl mx-auto flex items-center justify-between text-xs text-gray-400">
          <Link to={`/cities/${citySlug}/council`} className="hover:text-gray-600">
            ← All {displayName} council coverage
          </Link>
          <Link to="/" className="hover:text-gray-600">Helpped.com</Link>
        </div>
      </footer>
    </div>
  );
}
